import { iconNavAdicionar1 } from '@bsds-lib-component/icons/icons';
import { BsdsCard } from '@bsds-lib-component/react';
import React from 'react';

import type { BeneficiariosTableProps, BeneficiarioTableItem } from '../../../types/vida/table/beneficiariosTableType';
import { formatBeneficiarioPercentual } from '../../../utils/selfBillingUtil';
import Button from '../../button/Button';
import Tooltip from '../../tooltip/Tooltip';
import TableItemActionsMenu from '../shared/TableItemActionsMenu';
import TableMenuItemButton from '../shared/TableMenuItemButton';
import { createMenuItemActionHandlers } from '../shared/tableMenuUtils';
import { useTableItemMenu } from '../shared/useTableItemMenu';

const BeneficiariosTable: React.FC<BeneficiariosTableProps> = ({
  rows,
  onEdit,
  onAdd,
  canEdit = true,
  emptyMessage = 'Nenhum Beneficiário cadastrado',
}) => {
  const {
    closeItemMenu,
    isItemMenuOpen,
    handleItemMenuToggle,
    handleItemMenuKeyDown,
    focusMenuButton,
    registerItemMenuRef,
    registerItemMenuButtonRef,
    registerItemActionRef,
  } = useTableItemMenu<string>();

  const editActionHandlers = createMenuItemActionHandlers<BeneficiarioTableItem>({
    onAction: onEdit,
    closeMenu: closeItemMenu,
    focusMenuButton: (item) => focusMenuButton(item.id),
  });

  return (
    <BsdsCard className="bsds-p-16">
      <div className="bsds-display-flex bsds-justify-content-between bsds-align-items-center bsds-mb-16 gap-4">
        <div className="bsds-display-flex bsds-align-items-center">
          <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
          <h4 className="opacity-80">Beneficiários</h4>
        </div>
        {onAdd && (
          <Button
            type="button"
            color="primary"
            fill="solid"
            icon={iconNavAdicionar1}
            iconPosition="start"
            onClick={onAdd}
            disabled={!canEdit}
            aria-label="Adicionar beneficiário"
            aria-disabled={!canEdit}
          >
            Adicionar
          </Button>
        )}
      </div>

      <div className="w-full overflow-x-auto custom-scrollbar rounded-md shadow-xs">
        <table className="min-w-[720px] w-full table-fixed" aria-label="Tabela de beneficiários do segurado">
          <thead>
            <tr>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-16" scope="col">
                <Tooltip label="Ações">
                  <span>Ações</span>
                </Tooltip>
              </th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Nome</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Parentesco</th>
              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap w-32" scope="col">Percentual</th>
            </tr>
          </thead>

          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
            {rows.length > 0 ? (
              rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50 transition-colors text-xs">
                  <td className="px-4 py-2 whitespace-nowrap" onClick={(event) => event.stopPropagation()}>
                    <TableItemActionsMenu
                      itemId={row.id}
                      isOpen={isItemMenuOpen(row.id)}
                      menuId={`beneficiario-menu-${row.id}`}
                      triggerLabel={`Ações do beneficiário ${row.nomeBeneficiario}`}
                      menuLabel={`Menu de ações do beneficiário ${row.nomeBeneficiario}`}
                      triggerTooltip={`Ações do beneficiário ${row.nomeBeneficiario}`}
                      onToggle={(event) => handleItemMenuToggle(row.id, event)}
                      onKeyDown={(event) => handleItemMenuKeyDown(row.id, event)}
                      registerMenuRef={(element) => registerItemMenuRef(row.id, element)}
                      registerButtonRef={(element) => registerItemMenuButtonRef(row.id, element)}
                    >
                      <TableMenuItemButton
                        registerRef={(element) => registerItemActionRef(row.id, element)}
                        onClick={(event) => editActionHandlers.handleClick(row, event)}
                        onKeyDown={(event) => editActionHandlers.handleKeyDown(row, event)}
                      >
                        {canEdit ? 'Editar' : 'Visualizar'}
                      </TableMenuItemButton>
                    </TableItemActionsMenu>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">
                    <Tooltip label={row.nomeBeneficiario}>
                      <span>{row.nomeBeneficiario}</span>
                    </Tooltip>
                  </td>
                  <td className="px-4 py-2 whitespace-nowrap">{row.parentesco}</td>
                  <td className="px-4 py-2 whitespace-nowrap">{formatBeneficiarioPercentual(row.percentual)}</td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={4} className="px-4 py-4 text-center text-text-color text-xs" role="status" aria-live="polite">
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </BsdsCard>
  );
};

export default BeneficiariosTable;
